/**
 * ==========================================================================
 * FAQ UNIVERSAL JAVASCRIPT - UNIFIED FOR ALL PAGES
 * ==========================================================================
 * 
 * File: assets/js/faq-universal.js
 * Usage: Include in all pages that need FAQ functionality
 * Compatible with: All page templates and components
 */

class FAQUniversal {
    constructor(options = {}) {
        this.options = {
            faqSelector: '.faq-item', // Unified to single format
            questionSelector: '.faq-question', // Unified format
            answerSelector: '.faq-answer', // Unified format
            toggleSelector: '.faq-toggle', // Unified format
            activeClass: 'active',
            accordionBehavior: true, // Only one FAQ open at a time
            smoothScroll: false, // Disabled scroll to improve navigation
            scrollOffset: 100,
            debug: false,
            ...options
        };
        
        this.faqItems = [];
        this.init();
    }
    
    init() {
        this.log('🔄 Initializing FAQ Universal System');
        this.bindEvents();
        this.setupAccessibility();
        this.log('✅ FAQ Universal System initialized successfully');
    }
    
    bindEvents() {
        // Use event delegation for better performance
        document.addEventListener('click', (e) => {
            const faqQuestion = e.target.closest(this.options.questionSelector);
            if (faqQuestion) {
                e.preventDefault();
                this.handleFAQClick(faqQuestion);
            }
        });
        
        // Keyboard accessibility
        document.addEventListener('keydown', (e) => {
            const faqQuestion = e.target.closest(this.options.questionSelector);
            if (faqQuestion && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                this.handleFAQClick(faqQuestion);
            }
        });
        
        this.log('📌 Event listeners attached');
    }
    
    handleFAQClick(questionElement) {
        const faqItem = questionElement.closest(this.options.faqSelector);
        if (!faqItem) return;
        
        const isActive = faqItem.classList.contains(this.options.activeClass);
        
        this.log(`🎯 FAQ clicked: ${questionElement.textContent.trim().substring(0, 50)}...`);
        console.log('🔍 FAQ Debug - Element:', faqItem);
        console.log('🔍 FAQ Debug - isActive:', isActive);
        console.log('🔍 FAQ Debug - Answer element:', faqItem.querySelector(this.options.answerSelector));
        
        if (this.options.accordionBehavior) {
            // Close all other FAQs
            this.closeAllFAQs();
        }
        
        if (isActive && this.options.accordionBehavior) {
            // Close if it was already active
            this.closeFAQ(faqItem);
        } else {
            // Open the clicked FAQ
            this.openFAQ(faqItem);
        }
        
        // Smooth scroll to FAQ if needed
        if (this.options.smoothScroll && !isActive) {
            this.scrollToFAQ(faqItem);
        }
    }
    
    openFAQ(faqItem) {
        const answer = faqItem.querySelector(this.options.answerSelector);
        const toggle = faqItem.querySelector(this.options.toggleSelector);
        
        console.log('🔧 Opening FAQ - Answer element:', answer);
        
        faqItem.classList.add(this.options.activeClass);
        
        if (answer) {
            // Ensure it's visible first
            answer.style.display = 'block';
            
            // Calculate actual height needed
            const currentMaxHeight = answer.style.maxHeight;
            answer.style.maxHeight = 'none';
            const height = answer.scrollHeight;
            answer.style.maxHeight = currentMaxHeight || '0';
            
            console.log('📏 FAQ Height calculated:', height + 'px');
            
            // Trigger reflow and animate
            requestAnimationFrame(() => {
                answer.style.maxHeight = height + 'px';
                console.log('🎬 FAQ Animation applied');
                
                // Remove max-height restriction after animation completes
                setTimeout(() => {
                    if (faqItem.classList.contains(this.options.activeClass)) {
                        answer.style.maxHeight = 'none';
                        console.log('🔓 FAQ max-height restriction removed');
                    }
                }, 300);
            });
        }
        
        // Update toggle indicator
        if (toggle) {
            toggle.textContent = '-';
        }
        
        this.log('📖 FAQ opened');
        
        // Fire custom event
        this.fireEvent(faqItem, 'faq:opened');
    }
    
    closeFAQ(faqItem) {
        const answer = faqItem.querySelector(this.options.answerSelector);
        const toggle = faqItem.querySelector(this.options.toggleSelector);
        
        faqItem.classList.remove(this.options.activeClass);
        
        if (answer) {
            answer.style.maxHeight = '0';
            // Hide after animation completes
            setTimeout(() => {
                if (!faqItem.classList.contains(this.options.activeClass)) {
                    answer.style.display = 'none';
                }
            }, 300);
        }
        
        // Update toggle indicator
        if (toggle) {
            toggle.textContent = '+';
        }
        
        this.log('📕 FAQ closed');
        
        // Fire custom event
        this.fireEvent(faqItem, 'faq:closed');
    }
    
    closeAllFAQs() {
        const allFAQs = document.querySelectorAll(`${this.options.faqSelector}.${this.options.activeClass}`);
        allFAQs.forEach(faq => this.closeFAQ(faq));
    }
    
    scrollToFAQ(faqItem) {
        const rect = faqItem.getBoundingClientRect();
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = rect.top + scrollTop - this.options.scrollOffset;
        
        window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
        });
    }
    
    setupAccessibility() {
        const questions = document.querySelectorAll(this.options.questionSelector);
        questions.forEach((question, index) => {
            const faqItem = question.closest(this.options.faqSelector);
            const answer = faqItem.querySelector(this.options.answerSelector);
            
            // Set ARIA attributes
            question.setAttribute('aria-expanded', 'false');
            question.setAttribute('aria-controls', `faq-answer-${index}`);
            question.setAttribute('tabindex', '0');
            
            if (answer) {
                answer.setAttribute('id', `faq-answer-${index}`);
                answer.setAttribute('aria-labelledby', `faq-question-${index}`);
            }
            
            question.setAttribute('id', `faq-question-${index}`);
        });
        
        this.log('♿ Accessibility attributes set');
    }
    
    // Public API methods
    openFAQById(faqId) {
        const faq = document.getElementById(faqId);
        if (faq) this.openFAQ(faq);
    }
    
    closeFAQById(faqId) {
        const faq = document.getElementById(faqId);
        if (faq) this.closeFAQ(faq);
    }
    
    toggleAccordionBehavior(enabled = true) {
        this.options.accordionBehavior = enabled;
        this.log(`🎛️ Accordion behavior ${enabled ? 'enabled' : 'disabled'}`);
    }
    
    // Utility methods
    fireEvent(element, eventName, data = {}) {
        const event = new CustomEvent(eventName, {
            detail: { element, ...data },
            bubbles: true
        });
        element.dispatchEvent(event);
    }
    
    log(message) {
        if (this.options.debug) {
            console.log(`[FAQ Universal] ${message}`);
        }
    }
    
    destroy() {
        // Clean up event listeners and reset states
        this.closeAllFAQs();
        this.log('🗑️ FAQ Universal System destroyed');
    }
}

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
    // Check if FAQs exist on the page
    const faqsExist = document.querySelector('.faq-item');
    
    if (faqsExist) {
        // Initialize with default options
        window.FAQUniversalInstance = new FAQUniversal({
            debug: false // Set to true for debugging
        });
        
        console.log('✅ FAQ Universal System auto-initialized');
    }
});

// Expose class globally for custom implementations
window.FAQUniversal = FAQUniversal;